// Agents list. Table-shaped instead of cards because operators want to scan
// model + last-updated quickly. Delete is a Radix Dialog confirm so a stray
// click on the row icon doesn't nuke an agent.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, AlertTriangle, Bot, MoreHorizontal, Plus, Power, Sparkles, Trash2 } from 'lucide-react';
import type { Agent } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { friendlyError } from '../../lib/error-message.js';
import { agentsApi, AGENTS_QUERY_KEY } from '../../lib/api/agents.js';
import { useEnableAgent } from '../../lib/api/billing.js';
import { formatDate } from '../../lib/format-date.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardContent } from '../../components/ui/card.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { toast } from '../../components/ui/toaster.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../components/ui/tooltip.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function relativeTime(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < hour) return RELATIVE.format(Math.round(ms / minute), 'minute');
  if (abs < day) return RELATIVE.format(Math.round(ms / hour), 'hour');
  if (abs < 7 * day) return RELATIVE.format(Math.round(ms / day), 'day');
  return formatDate(iso);
}

export function AgentsListPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<Agent | null>(null);

  const list = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: () => agentsApi.list(api),
  });

  const remove = useMutation({
    mutationFn: (id: string) => agentsApi.remove(api, id),
    onSuccess: (_, id) => {
      const removed = pendingDelete?.id === id ? pendingDelete : null;
      void queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      toast.success(removed ? `Deleted ${removed.name}` : 'Agent deleted');
    },
    onError: (err) => {
      toast.error('Could not delete agent', { description: friendlyError(err) });
    },
    onSettled: () => { setPendingDelete(null); },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agents"
        description="Reusable agent configurations: prompt, model, tools, budget."
        actions={
          <Button asChild>
            <Link to="/agents/new">
              <Plus aria-hidden /> New agent
            </Link>
          </Button>
        }
      />

      {list.isLoading && <TableSkeleton />}
      {list.isError && (
        <ErrorRetry
          title="Could not load agents"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      )}
      {list.data?.agents.length === 0 && <AgentsEmptyState />}
      {list.data && list.data.agents.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Tools</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-12 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.agents.map((agent) => (
                  <TableRow key={agent.id} className={agent.disabledAt ? 'opacity-60' : undefined}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span
                          aria-hidden
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                            agent.disabledAt
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-primary/10 text-primary'
                          }`}
                        >
                          {agent.disabledAt ? <Archive className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                        </span>
                        <Link
                          to={`/agents/${agent.id}`}
                          className="font-medium underline-offset-4 hover:underline"
                        >
                          {agent.name}
                        </Link>
                        {agent.disabledAt && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" tabIndex={0} className="cursor-help gap-1">
                                <Archive className="h-3 w-3" aria-hidden />
                                Disabled
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {disabledTooltipFor(agent.disabledReason)}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {agent.isSample && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* `tabIndex` so keyboard users can reach the
                                  tooltip even though Badge is not focusable
                                  by default. */}
                              <Badge variant="secondary" tabIndex={0} className="cursor-help">
                                Sample
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              Created by the welcome tour. Doesn&apos;t count against your plan&apos;s
                              agent limit — delete it anytime.
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {agent.planMismatch && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="destructive"
                                tabIndex={0}
                                className="cursor-help gap-1"
                              >
                                <AlertTriangle className="h-3 w-3" aria-hidden />
                                Plan mismatch
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              This agent uses a model not available on your current plan.
                              Chat calls will be rejected — edit the agent to pick a permitted
                              model, or upgrade.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={agent.planMismatch ? 'destructive' : 'muted'}
                        className="font-mono text-[11px]"
                      >
                        {agent.modelPolicy.provider}/{agent.modelPolicy.model}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="tabular-nums">
                        {agent.tools.length}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(agent.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions agent={agent} onDelete={() => { setPendingDelete(agent); }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <DeleteAgentDialog
        agent={pendingDelete}
        onClose={() => { setPendingDelete(null); }}
        onConfirm={(id) => { remove.mutate(id); }}
        pending={remove.isPending}
      />
    </div>
  );
}

function DeleteAgentDialog({
  agent,
  onClose,
  onConfirm,
  pending,
}: {
  agent: Agent | null;
  onClose: () => void;
  onConfirm: (id: string) => void;
  pending: boolean;
}) {
  return (
    <Dialog
      open={agent !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete agent?</DialogTitle>
          <DialogDescription>
            {agent ? (
              <>
                <span className="font-medium text-foreground">{agent.name}</span> will be
                permanently deleted. Embed snippets that reference this agent will stop working.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={pending || !agent}
            onClick={() => {
              if (agent) onConfirm(agent.id);
            }}
          >
            {pending ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function disabledTooltipFor(reason: string | null): string {
  if (reason === 'plan_downgrade')
    return 'Disabled when you switched to a smaller plan that does not include this many agents. Re-enable on upgrade or remove permanently.';
  if (reason === 'cancel_to_free')
    return 'Disabled when your subscription ended and the tenant fell to the Free tier. Re-enable on upgrade or remove permanently.';
  return 'Disabled. Re-enable to use this agent again, or remove permanently.';
}

// Re-enable affordance copy. Used as tooltip on the inline button + as the
// dropdown item label so the action's framing reads consistently across
// both surfaces.
const REENABLE_TOOLTIP =
  'Re-enable this agent. Disabled by a recent plan change — your data was preserved. Click to use it again.';

// Per-row actions cell. Composes:
//   • Inline labeled "Re-enable" button (only on disabled rows) — Linear /
//     Vercel / GitHub all use a labeled inline button rather than icon-only
//     for archived-item recovery, because the action is rare but high-stakes
//     and customers benefit from immediate certainty about what it does.
//     Wrapped in <Tooltip> for the longer "why" copy.
//   • Dropdown menu — same Re-enable item lives here too so the action is
//     discoverable through the standard "all actions in the menu" pattern
//     the rest of the dashboard follows. Edit / Re-enable (when disabled) /
//     Delete.
function RowActions({ agent, onDelete }: { agent: Agent; onDelete: () => void }) {
  const enable = useEnableAgent();
  const isPending = enable.isPending;

  const fireEnable = () => {
    enable.mutate(agent.id, {
      onSuccess: () => { toast.success(`Re-enabled ${agent.name}`); },
      onError: (err) => {
        toast.error('Could not re-enable agent', { description: friendlyError(err) });
      },
    });
  };

  return (
    <div className="flex items-center justify-end gap-2">
      {agent.disabledAt && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              onClick={fireEnable}
              disabled={isPending}
              aria-label={`Re-enable ${agent.name}`}
            >
              <Power aria-hidden />
              {isPending ? 'Re-enabling…' : 'Re-enable'}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{REENABLE_TOOLTIP}</TooltipContent>
        </Tooltip>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon-sm" variant="ghost" aria-label={`Actions for ${agent.name}`}>
            <MoreHorizontal aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/agents/${agent.id}`}>Edit</Link>
          </DropdownMenuItem>
          {agent.disabledAt && (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                fireEnable();
              }}
              disabled={isPending}
              aria-label={`Re-enable ${agent.name}`}
            >
              <Power aria-hidden /> Re-enable
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onDelete();
            }}
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            aria-label={`Delete ${agent.name}`}
          >
            <Trash2 aria-hidden /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function TableSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-7 w-7 rounded-md" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="ml-auto h-5 w-24" />
              <Skeleton className="h-5 w-12" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AgentsEmptyState() {
  const api = useApiClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const tryDemo = useMutation({
    mutationFn: () => agentsApi.createSample(api),
    onSuccess: ({ agentId }) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      navigate(`/agents/${agentId}?tab=playground`);
    },
    onError: (err) => {
      setError(friendlyError(err, 'Could not create demo agent.'));
    },
  });

  return (
    <div className="space-y-3">
      <EmptyState
        icon={Bot}
        title="No agents yet"
        description="Spin up a working AI assistant in seconds. We'll create a demo agent so you can see it in action — then customize or build your own."
        action={
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => { tryDemo.mutate(); }}
              disabled={tryDemo.isPending}
              aria-label="Try a sample agent"
            >
              <Sparkles aria-hidden />
              {tryDemo.isPending ? 'Preparing your assistant…' : 'Try a sample agent'}
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/agents/new">
                <Plus aria-hidden /> Create from scratch
              </Link>
            </Button>
          </div>
        }
      />
      {error && (
        <p role="alert" className="text-center text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
